import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FishRive, { type FishHandle } from './FishRive';

// ─────────────────────────────────────────────────────────────────────────────
// One block in the student's feed: a fish on the left, the task beside it.
//
// The fish behaves exactly as on the old fish screen: hovering (or focusing)
// the block drives the Rive `Focused` bool, and a click fires the `Selected`
// trigger and lets it play before the page changes.
// ─────────────────────────────────────────────────────────────────────────────

/** How long the Selected animation plays before navigating. */
const SELECT_LEAD_MS = 650;

export type Tone = 'todo' | 'overdue' | 'done' | 'quiet';

type Props = {
  riv: string;
  to: string;
  /** "Video", "Flashcards"… */
  label: string;
  title: string;
  /** The line under the title: progress, due date, why it's here. */
  detail?: string;
  /** The chip on the right: "2 of 5", "12 due", "✓". */
  chip?: string;
  tone?: Tone;
  /** 0–1: a thin progress bar along the bottom of the block. */
  progress?: number;
  size?: number;
};

export default function FishTask({
  riv, to, label, title, detail, chip, tone = 'todo', progress, size = 96,
}: Props) {
  const navigate = useNavigate();
  const fish = useRef<FishHandle>(null);
  const [focused, setFocused] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const open = () => {
    if (leaving) return;
    setLeaving(true);
    setFocused(true);
    fish.current?.fireSelected();
    window.setTimeout(() => navigate(to), SELECT_LEAD_MS);
  };

  return (
    <div
      className={`task task-${tone}${focused ? ' on' : ''}`}
      role="link"
      tabIndex={0}
      aria-label={`${label}: ${title}${detail ? `, ${detail}` : ''}`}
      onPointerEnter={() => !leaving && setFocused(true)}
      onPointerLeave={() => !leaving && setFocused(false)}
      onFocus={() => !leaving && setFocused(true)}
      onBlur={() => !leaving && setFocused(false)}
      onClick={open}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
    >
      <div className="task-fish" style={{ width: size, height: size }}>
        <FishRive ref={fish} src={riv} focused={focused || leaving} size={size} />
      </div>
      <div className="task-text">
        <span className="task-label">{label}</span>
        <span className="task-title">{title}</span>
        {detail && <span className="task-detail">{detail}</span>}
      </div>
      {chip && <span className="task-chip">{chip}</span>}
      <span className="task-chev" aria-hidden>›</span>
      {progress !== undefined && (
        <span className="task-progress"><i style={{ width: `${Math.round(Math.min(1, progress) * 100)}%` }} /></span>
      )}
    </div>
  );
}
